import { SCROLL_AXES, type ScrollAxis } from "./scroll-box-axes"
import { collectContentWithin, contentSpans, innerBoxSpan, isShownToUsers } from "./scroll-box-content-leaves"
import type { CutOffContentFinding } from "./scroll-box-findings"
import { identifyScrollBox } from "./scroll-box-identity"
import { overhangPx, scrollportSpan, type PixelSpan } from "./scroll-box-spans"
import {
  COLLAPSED_EXTENT_PX,
  OVERFLOW_THRESHOLD_PX,
  clipsAlong,
  escapesClipOf,
  hasOwnText,
  isPageRoot,
  overflowsAlong,
} from "./scroll-box-styles"

const COLLAPSED_TOGGLE_SELECTOR = '[aria-expanded="false"]'

function isLineClamped(style: CSSStyleDeclaration): boolean {
  const lineClamp = style.getPropertyValue("-webkit-line-clamp")
  return lineClamp !== "" && lineClamp !== "none"
}

function truncatesItsOwnText(element: Element): boolean {
  const style = getComputedStyle(element)
  return style.textOverflow === "ellipsis" || isLineClamped(style)
}

function isControlledByCollapsedToggle(element: Element): boolean {
  for (let ancestor: Element | null = element; ancestor; ancestor = ancestor.parentElement) {
    if (ancestor.id && document.querySelector(`${COLLAPSED_TOGGLE_SELECTOR}[aria-controls~="${CSS.escape(ancestor.id)}"]`)) {
      return true
    }
  }
  return false
}

function cutsDeliberately(clip: Element, axis: ScrollAxis): boolean {
  return (
    clip[axis.clientSize] <= COLLAPSED_EXTENT_PX ||
    truncatesItsOwnText(clip) ||
    clip.closest(COLLAPSED_TOGGLE_SELECTOR) !== null ||
    isControlledByCollapsedToggle(clip)
  )
}

function clippedContentSpans(clip: Element, axis: ScrollAxis): PixelSpan[] {
  const content = collectContentWithin(clip, axis, escapesClipOf)
  return [
    ...(hasOwnText(clip) ? contentSpans(clip, axis) : []),
    ...content.leaves.flatMap((leaf) => contentSpans(leaf, axis)),
    ...content.innerBoxes.map((innerBox) => innerBoxSpan(innerBox, axis)),
  ]
}

function cutOffPxAlong(clip: Element, axis: ScrollAxis): number {
  const visibleSpan = scrollportSpan(clip, axis)
  return Math.max(0, ...clippedContentSpans(clip, axis).map((span) => overhangPx(span, visibleSpan)))
}

function cutsContentAlong(clip: Element, axis: ScrollAxis): boolean {
  return clipsAlong(clip, axis) && overflowsAlong(clip, axis) && !cutsDeliberately(clip, axis)
}

export function detectCutOffContent(element: Element): CutOffContentFinding[] {
  if (isPageRoot(element) || !isShownToUsers(element)) return []
  return SCROLL_AXES.filter((axis) => cutsContentAlong(element, axis)).flatMap((axis) => {
    const cutOffPx = cutOffPxAlong(element, axis)
    if (cutOffPx <= OVERFLOW_THRESHOLD_PX) return []
    const finding: CutOffContentFinding = {
      kind: "cut-off-content",
      identity: identifyScrollBox(element),
      boxWidthPx: element.clientWidth,
      boxHeightPx: element.clientHeight,
      axis: axis.name,
      cutOffPx,
    }
    return [finding]
  })
}
